import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Pencil, Tag as TagIcon } from "lucide-react";
import { ColorSwatchGrid, DEFAULT_PALETTE } from "@/components/ColorSwatchGrid";

const PALETTE = DEFAULT_PALETTE;

export default function Tags({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: PALETTE[0] });

  const load = async () => setItems((await api.get("/tags")).data);
  useEffect(() => { load(); }, []);

  const openForm = (t = null) => {
    if (t) { setEditing(t.id); setForm({ name: t.name, color: t.color || PALETTE[0] }); }
    else { setEditing(null); setForm({ name: "", color: PALETTE[0] }); }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/tags/${editing}`, form);
      else await api.post("/tags", form);
      toast.success(editing ? "Tag aggiornato" : "Tag creato");
      setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo tag? I clienti che lo hanno assegnato lo perderanno.")) return;
    await api.delete(`/tags/${id}`);
    toast.success("Tag rimosso");
    load();
  };

  return (
    <div data-testid="tags-page" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className={`font-display font-black tracking-tight inline-flex items-center gap-3 ${embedded ? "text-xl" : "text-4xl"}`}>
            <TagIcon className={`${embedded ? "h-5 w-5" : "h-7 w-7"} text-[color:var(--sp-brand)]`} />
            Tag
          </h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2 text-sm">Crea etichette per segmentare la tua clientela: VIP, frequente, colore, ecc.</p>
        </div>
        <button data-testid="tags-new-btn" onClick={() => openForm()} className="sp-btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Nuovo tag
        </button>
      </header>

      {items.length === 0 ? (
        <div className="sp-card p-12 text-center text-[color:var(--sp-text-secondary)]">
          <TagIcon className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Nessun tag ancora. Creane uno per iniziare a segmentare.
        </div>
      ) : (
        <div className="sp-card p-6">
          <div className="flex flex-wrap gap-2">
            {items.map((t) => (
              <div key={t.id} data-testid={`tag-item-${t.id}`}
                   className="group inline-flex items-center gap-2 rounded-full pl-3 pr-1 py-1 border"
                   style={{ borderColor: `${t.color}55`, background: `${t.color}12` }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                <span className="text-sm font-bold" style={{ color: t.color }}>#{t.name}</span>
                <button onClick={() => openForm(t)} className="p-1 opacity-40 group-hover:opacity-100 hover:text-[color:var(--sp-brand)]"><Pencil className="h-3 w-3" /></button>
                <button onClick={() => remove(t.id)} className="p-1 opacity-40 group-hover:opacity-100 hover:text-[color:var(--sp-danger)]"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="sp-card w-full max-w-md p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black">{editing ? "Modifica tag" : "Nuovo tag"}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-bold">Nome tag</label>
                <input data-testid="tag-name-input" required placeholder="es. VIP, colore, fedele"
                       className="sp-input mt-1"
                       value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-bold">Colore</label>
                <ColorSwatchGrid
                  value={form.color}
                  onChange={(c) => setForm({ ...form, color: c })}
                  testIdPrefix="tag-color"
                  className="mt-2"
                />
              </div>
              <div className="p-3 rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/30">
                <div className="text-xs text-[color:var(--sp-text-muted)] mb-2">Anteprima</div>
                <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 border"
                      style={{ borderColor: `${form.color}55`, background: `${form.color}12`, color: form.color }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: form.color }} />
                  <span className="text-sm font-bold">#{form.name || "nome-tag"}</span>
                </span>
              </div>
              <button data-testid="tag-submit-btn" className="sp-btn-primary w-full">{editing ? "Salva modifiche" : "Crea tag"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
